import React, { useEffect, useRef, useState } from 'react';
import { ReviewMask } from '../../../sdk/@types/Revisao';

type Mode = 'draw' | 'move' | 'resize';
type HandlePos = 'nw' | 'ne' | 'sw' | 'se' | 'e' | 's';

const HANDLE_PX = 12;
// Touch needs a noticeably bigger hit box than a mouse cursor can afford.
const HANDLE_PX_TOUCH = 22;
/** Anything smaller than this is a stray click, not a mask. */
const MIN_SIZE_PCT = 1.5;

interface Point {
  x: number;
  y: number;
}

interface DragState {
  mode: Mode;
  startPct: Point;
  maskAtStart: ReviewMask | null;
  index: number;
  pointerId: number;
  /** Which of the six handles started this drag; only set when mode is 'resize'. */
  handle?: HandlePos;
}

interface UseMaskDrawOptions {
  masks: ReviewMask[];
  selectedIndex: number | null;
  imageRef: React.RefObject<HTMLImageElement | null>;
  disabled?: boolean;
  onChange: (masks: ReviewMask[]) => void;
  onSelect: (index: number | null) => void;
}

const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));

/** Resize `base` from one of the six handles, given the pointer's % delta since
 *  drag start. Each handle pins the opposite side(s) in place. */
function resizeFromHandle(base: ReviewMask, handle: HandlePos, dx: number, dy: number): ReviewMask {
  switch (handle) {
    case 'nw': {
      const x = clamp(base.x + dx, 0, base.x + base.w - MIN_SIZE_PCT);
      const y = clamp(base.y + dy, 0, base.y + base.h - MIN_SIZE_PCT);
      return { ...base, x, y, w: base.x + base.w - x, h: base.y + base.h - y };
    }
    case 'ne': {
      const w = clamp(base.w + dx, MIN_SIZE_PCT, 100 - base.x);
      const y = clamp(base.y + dy, 0, base.y + base.h - MIN_SIZE_PCT);
      return { ...base, w, y, h: base.y + base.h - y };
    }
    case 'sw': {
      const x = clamp(base.x + dx, 0, base.x + base.w - MIN_SIZE_PCT);
      const h = clamp(base.h + dy, MIN_SIZE_PCT, 100 - base.y);
      return { ...base, x, w: base.x + base.w - x, h };
    }
    case 'se': {
      const w = clamp(base.w + dx, MIN_SIZE_PCT, 100 - base.x);
      const h = clamp(base.h + dy, MIN_SIZE_PCT, 100 - base.y);
      return { ...base, w, h };
    }
    case 'e':
      return { ...base, w: clamp(base.w + dx, MIN_SIZE_PCT, 100 - base.x) };
    case 's':
      return { ...base, h: clamp(base.h + dy, MIN_SIZE_PCT, 100 - base.y) };
  }
}

/** Which side(s) of `base` a corner handle's opposite corner sits on, as % of
 *  the image box — the most that handle's w/h can grow toward it. */
function cornerAxisMax(base: ReviewMask, handle: 'nw' | 'ne' | 'sw' | 'se') {
  return {
    wMax: handle === 'nw' || handle === 'sw' ? base.x + base.w : 100 - base.x,
    hMax: handle === 'nw' || handle === 'ne' ? base.y + base.h : 100 - base.y,
  };
}

/** Rebuilds the box for a corner handle from a new w/h, keeping the opposite
 *  corner pinned — the same per-corner geometry `resizeFromHandle` encodes. */
function cornerBox(base: ReviewMask, handle: 'nw' | 'ne' | 'sw' | 'se', w: number, h: number): ReviewMask {
  const x = handle === 'nw' || handle === 'sw' ? base.x + base.w - w : base.x;
  const y = handle === 'nw' || handle === 'ne' ? base.y + base.h - h : base.y;
  return { ...base, x, y, w, h };
}

/**
 * Shift-held variant of `resizeFromHandle`: keeps the mask's on-screen aspect
 * ratio fixed while a corner handle drags. `boxW`/`boxH` are the rendered image
 * box's pixel size — % of width and % of height are different pixel scales
 * unless that box happens to be square, so the ratio has to be judged in
 * pixels even though the box it returns is still stored in %.
 *
 * Only meaningful for the four corners: an edge-mid handle already resizes a
 * single axis, so it falls back to the free resize unchanged.
 */
function resizeFromHandleKeepingRatio(
  base: ReviewMask,
  handle: HandlePos,
  dx: number,
  dy: number,
  boxW: number,
  boxH: number,
): ReviewMask {
  if (handle !== 'nw' && handle !== 'ne' && handle !== 'sw' && handle !== 'se') {
    return resizeFromHandle(base, handle, dx, dy);
  }
  if (base.w <= 0 || base.h <= 0 || boxW <= 0 || boxH <= 0) {
    return resizeFromHandle(base, handle, dx, dy);
  }

  const ratio = (base.w * boxW) / (base.h * boxH);
  const { wMax, hMax } = cornerAxisMax(base, handle);
  const free = resizeFromHandle(base, handle, dx, dy);
  // Whichever axis the pointer moved more drives the resize; the other is derived from it.
  const driveByWidth = Math.abs(free.w - base.w) * boxW >= Math.abs(free.h - base.h) * boxH;

  let w: number;
  let h: number;
  if (driveByWidth) {
    w = free.w;
    h = clamp((w * boxW) / (ratio * boxH), MIN_SIZE_PCT, hMax);
    w = clamp((h * ratio * boxH) / boxW, MIN_SIZE_PCT, wMax);
  } else {
    h = free.h;
    w = clamp((h * ratio * boxH) / boxW, MIN_SIZE_PCT, wMax);
    h = clamp((w * boxW) / (ratio * boxH), MIN_SIZE_PCT, hMax);
  }
  return cornerBox(base, handle, w, h);
}

/** Shift-held variant of the free-draw rect: grows a pixel-accurate square from
 *  the start point toward the cursor, clamped to stay inside the image box. */
function squareDraftFromStart(
  start: Point,
  cx: number,
  cy: number,
  boxW: number,
  boxH: number,
): ReviewMask {
  const dxPx = ((cx - start.x) / 100) * boxW;
  const dyPx = ((cy - start.y) / 100) * boxH;
  const signX = dxPx < 0 ? -1 : 1;
  const signY = dyPx < 0 ? -1 : 1;
  const maxPx = Math.min(
    signX > 0 ? ((100 - start.x) / 100) * boxW : (start.x / 100) * boxW,
    signY > 0 ? ((100 - start.y) / 100) * boxH : (start.y / 100) * boxH,
  );
  const sidePx = Math.min(Math.max(Math.abs(dxPx), Math.abs(dyPx)), maxPx);
  const w = (sidePx / boxW) * 100;
  const h = (sidePx / boxH) * 100;
  return {
    x: signX > 0 ? start.x : start.x - w,
    y: signY > 0 ? start.y : start.y - h,
    w,
    h,
    label: '',
  };
}

/**
 * Draw / move / resize several masks over one image, all in % of the rendered
 * image box so the coords survive any display size.
 *
 * Sibling of PdfAnnotator's `useHighlightDraw`, which handles a single rect keyed
 * to a page array and so can't express a mask list on one image.
 */
export default function useMaskDraw({
  masks,
  selectedIndex,
  imageRef,
  disabled = false,
  onChange,
  onSelect,
}: UseMaskDrawOptions) {
  const [draft, setDraft] = useState<ReviewMask | null>(null);
  const drag = useRef<DragState | null>(null);

  // Window listeners are registered once, so they read live values through this ref
  // instead of closing over a stale render.
  const latest = useRef({ masks, draft, disabled, onChange, onSelect });
  latest.current = { masks, draft, disabled, onChange, onSelect };

  const pctFromEvent = (clientX: number, clientY: number): Point | null => {
    const el = imageRef.current;
    if (!el) return null;
    const r = el.getBoundingClientRect();
    if (!r.width || !r.height) return null;
    return {
      x: ((clientX - r.left) / r.width) * 100,
      y: ((clientY - r.top) / r.height) * 100,
    };
  };

  const handlePointerDown = (e: React.PointerEvent) => {
    if (disabled) return;
    const el = imageRef.current;
    if (!el) return;

    const p = pctFromEvent(e.clientX, e.clientY);
    if (!p) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);

    const r = el.getBoundingClientRect();
    // Touch needs the bigger tolerance — a finger can't land as precisely as a cursor.
    const handlePx = e.pointerType === 'touch' ? HANDLE_PX_TOUCH : HANDLE_PX;
    const handleW = (handlePx / r.width) * 100;
    const handleH = (handlePx / r.height) * 100;

    // Any of the selected mask's six handles wins over everything under the cursor —
    // picked by nearest (normalized to the tolerance box) rather than list order, so a
    // corner's zone can't swallow an edge-mid's on a mask smaller than ~2x the tolerance.
    if (selectedIndex !== null && masks[selectedIndex]) {
      const m = masks[selectedIndex];
      const handlePoints: [HandlePos, Point][] = [
        ['nw', { x: m.x, y: m.y }],
        ['ne', { x: m.x + m.w, y: m.y }],
        ['sw', { x: m.x, y: m.y + m.h }],
        ['se', { x: m.x + m.w, y: m.y + m.h }],
        ['e', { x: m.x + m.w, y: m.y + m.h / 2 }],
        ['s', { x: m.x + m.w / 2, y: m.y + m.h }],
      ];
      let best: { handle: HandlePos; score: number } | null = null;
      for (const [handle, pt] of handlePoints) {
        const ndx = (p.x - pt.x) / handleW;
        const ndy = (p.y - pt.y) / handleH;
        if (Math.abs(ndx) <= 1 && Math.abs(ndy) <= 1) {
          const score = ndx * ndx + ndy * ndy;
          if (!best || score < best.score) best = { handle, score };
        }
      }
      if (best) {
        drag.current = {
          mode: 'resize',
          startPct: p,
          maskAtStart: m,
          index: selectedIndex,
          pointerId: e.pointerId,
          handle: best.handle,
        };
        return;
      }
    }

    // topmost mask under the cursor
    for (let i = masks.length - 1; i >= 0; i--) {
      const m = masks[i];
      if (p.x >= m.x && p.x <= m.x + m.w && p.y >= m.y && p.y <= m.y + m.h) {
        onSelect(i);
        drag.current = { mode: 'move', startPct: p, maskAtStart: m, index: i, pointerId: e.pointerId };
        return;
      }
    }

    onSelect(null);
    drag.current = { mode: 'draw', startPct: p, maskAtStart: null, index: -1, pointerId: e.pointerId };
    setDraft({ x: p.x, y: p.y, w: 0, h: 0, label: '' });
  };

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      const d = drag.current;
      if (!d || d.pointerId !== e.pointerId || latest.current.disabled) return;
      const el = imageRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      if (!r.width || !r.height) return;
      const p = pctFromEvent(e.clientX, e.clientY);
      if (!p) return;

      const { masks: cur, onChange: change } = latest.current;

      if (d.mode === 'draw') {
        // the pointer can leave the image mid-drag; keep the rect inside it
        const cx = clamp(p.x, 0, 100);
        const cy = clamp(p.y, 0, 100);
        if (e.shiftKey) {
          setDraft(squareDraftFromStart(d.startPct, cx, cy, r.width, r.height));
          return;
        }
        const x = Math.min(d.startPct.x, cx);
        const y = Math.min(d.startPct.y, cy);
        setDraft({
          x,
          y,
          w: Math.min(Math.abs(cx - d.startPct.x), 100 - x),
          h: Math.min(Math.abs(cy - d.startPct.y), 100 - y),
          label: '',
        });
        return;
      }

      const b = d.maskAtStart;
      if (!b) return;
      const dx = p.x - d.startPct.x;
      const dy = p.y - d.startPct.y;

      if (d.mode === 'move') {
        const x = clamp(b.x + dx, 0, 100 - b.w);
        const y = clamp(b.y + dy, 0, 100 - b.h);
        change(cur.map((m, i) => (i === d.index ? { ...m, x, y } : m)));
      } else {
        const resized = e.shiftKey
          ? resizeFromHandleKeepingRatio(b, d.handle ?? 'se', dx, dy, r.width, r.height)
          : resizeFromHandle(b, d.handle ?? 'se', dx, dy);
        change(cur.map((m, i) => (i === d.index ? resized : m)));
      }
    };

    const onUp = (e: PointerEvent) => {
      const d = drag.current;
      if (!d || d.pointerId !== e.pointerId) return;
      drag.current = null;
      if (d.mode !== 'draw') return;

      const { draft: pending, masks: cur, onChange: change, onSelect: select } = latest.current;
      if (pending && pending.w >= MIN_SIZE_PCT && pending.h >= MIN_SIZE_PCT) {
        change([...cur, pending]);
        select(cur.length);
      }
      setDraft(null);
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { draft, handlePointerDown };
}
